/**
 * Estado do formulário de criação/edição de contrato.
 * Concentra todos os useState do formulário, reset() e loadFromContract().
 * Usado por useContractForm().
 */
import { useState, useCallback, useEffect } from 'react';
import type { ContractResponseDto } from '../../services/contracts/ContractService';
import type { ClientResponse } from '../../types/dtos';
import { formatCepForInput, normalizeRepresentativePhoneInput } from '../../services/utils/stringUtils';
import type {
  ContractLineFormItem,
  ContractRepresentanteFormItem,
  ContractRecebedorFormItem,
  ContractTestemunhaFormItem,
} from '../../types/contractForm';
import {
  DEFAULT_CONTRACT_LINE_INITIAL,
  collectLinhasParaDesativarIds,
} from '../../types/contractForm';
import {
  hydrateSaleTokenFromContract,
  normalizeContractSaleTokenForForm,
} from '../../utils/contractClusterTokens';

export interface UseContractFormStateOptions {
  /** Callback opcional chamado após loadFromContract (ex.: para setContractUpdatedAt na página) */
  onLoadFromContract?: (contract: ContractResponseDto) => void;
}

const defaultLineEditing = { ...DEFAULT_CONTRACT_LINE_INITIAL };

export function useContractFormState(_options: UseContractFormStateOptions = {}) {
  const [tipoFatura, setTipoFatura] = useState('Mensal');
  const [envioFatura, setEnvioFatura] = useState('Email');
  const [origemVenda, setOrigemVenda] = useState('Site');

  const [cep, setCep] = useState('');
  const [cidade, setCidade] = useState('');
  const [estado, setEstado] = useState('');
  const [endereco, setEndereco] = useState('');
  const [numero, setNumero] = useState('');
  const [bairro, setBairro] = useState('');
  const [complemento, setComplemento] = useState('');
  const [referencia, setReferencia] = useState('');
  const [cepLoading, setCepLoading] = useState(false);

  const [adminNomeCompleto, setAdminNomeCompleto] = useState('');
  const [adminCpf, setAdminCpf] = useState('');
  const [adminRg, setAdminRg] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [adminTelefone1, setAdminTelefone1] = useState('');
  const [adminTelefone2, setAdminTelefone2] = useState('');
  const [adminSource, setAdminSource] = useState<'manual' | 'client' | 'partner' | null>(null);

  const [tipoContratacao, setTipoContratacao] = useState('');
  const [valorTotalMensal, setValorTotalMensal] = useState<number>(0);
  const [diaVencimento, setDiaVencimento] = useState<number>(10);
  const [numeroP2B, setNumeroP2B] = useState('');
  const [comprometimentoMinimo, setComprometimentoMinimo] = useState('');
  const [fastchip, setFastchip] = useState(false);
  // Pergunta obrigatoria na aba Contrato: "este contrato tera suspensao de linhas?"
  // null = ainda nao respondeu; bloqueia avanco. true = exige fastchip. false = livre.
  const [temSuspensao, setTemSuspensao] = useState<boolean | null>(null);

  const [representantes, setRepresentantes] = useState<ContractRepresentanteFormItem[]>([]);
  const [representanteManual, setRepresentanteManual] = useState({
    nome: '',
    cpf: '',
    rg: '',
    email: '',
    telefone: '',
  });
  const [selectedRepresentante, setSelectedRepresentante] = useState<ContractRepresentanteFormItem | null>(null);

  const [recebedores, setRecebedores] = useState<ContractRecebedorFormItem[]>([]);
  const [recebedorManual, setRecebedorManual] = useState({ nome: '', qualificacao: '', cpf: '', rg: '' });

  const [testemunhas, setTestemunhas] = useState<ContractTestemunhaFormItem[]>([]);
  const [testemunhaAtual, setTestemunhaAtual] = useState({ nome: '', cpf: '', rg: '' });

  const [linhas, setLinhas] = useState<ContractLineFormItem[]>([]);
  const [linhaAtual, setLinhaAtual] = useState({ ...DEFAULT_CONTRACT_LINE_INITIAL });
  const [linhasParaDesativar, setLinhasParaDesativar] = useState<Set<string>>(new Set());

  const [editingLinha, setEditingLinha] = useState<ContractLineFormItem | null>(null);
  const [showEditLinhaModal, setShowEditLinhaModal] = useState(false);
  const [linhaEditando, setLinhaEditando] = useState({ ...defaultLineEditing });

  const [needsIeMonitoring, setNeedsIeMonitoring] = useState(false);

  /**
   * Bypass de IE inapta confirmado pelo usuário no modal.
   * - false (default) — sem bypass; se IE inapta + Aditivo + !fastchip, bloqueia avanço.
   * - true — usuário marcou checkbox + escreveu justificativa; backend persiste como bypass.
   *
   * IMPORTANTE: este flag é AUTO-LIMPO sempre que `fastchip` virar true (ver ContractsPage),
   * pois bypass só faz sentido quando fastchip=false. Também é zerado em reset().
   */
  const [ieBypassConfirmed, setIeBypassConfirmed] = useState(false);
  const [ieBypassReason, setIeBypassReason] = useState('');

  const [isEditingExistingContract, setIsEditingExistingContract] = useState(false);
  const [tabValidationError, setTabValidationError] = useState('');
  const [error, setError] = useState('');
  const [isPrimeiraVenda, setIsPrimeiraVenda] = useState(false);
  /** '' no reset; em criação o fluxo da página exige token normalizado não vazio antes de avançar/salvar. */
  const [token, setToken] = useState('');

  useEffect(() => {
    setToken((prev) => hydrateSaleTokenFromContract(prev, fastchip));
  }, [fastchip]);

  const reset = useCallback(() => {
    setTipoFatura('Mensal');
    setEnvioFatura('Email');
    setOrigemVenda('Site');
    setCep('');
    setCidade('');
    setEstado('');
    setEndereco('');
    setNumero('');
    setBairro('');
    setComplemento('');
    setReferencia('');
    setAdminNomeCompleto('');
    setAdminCpf('');
    setAdminRg('');
    setAdminEmail('');
    setAdminTelefone1('');
    setAdminTelefone2('');
    setAdminSource(null);
    setTipoContratacao('');
    setValorTotalMensal(0);
    setDiaVencimento(10);
    setNumeroP2B('');
    setComprometimentoMinimo('');
    setFastchip(false);
    setTemSuspensao(null);
    setNeedsIeMonitoring(false);
    setIeBypassConfirmed(false);
    setIeBypassReason('');
    setIsEditingExistingContract(false);
    setRepresentantes([]);
    setRepresentanteManual({ nome: '', cpf: '', rg: '', email: '', telefone: '' });
    setSelectedRepresentante(null);
    setRecebedores([]);
    setRecebedorManual({ nome: '', qualificacao: '', cpf: '', rg: '' });
    setTestemunhas([]);
    setTestemunhaAtual({ nome: '', cpf: '', rg: '' });
    setLinhas([]);
    setLinhasParaDesativar(new Set());
    setLinhaAtual({ ...DEFAULT_CONTRACT_LINE_INITIAL });
    setEditingLinha(null);
    setShowEditLinhaModal(false);
    setLinhaEditando({ ...defaultLineEditing });
    setTabValidationError('');
    setError('');
    setIsPrimeiraVenda(false);
    setToken('');
  }, []);

  const loadFromContract = useCallback(
    (contract: ContractResponseDto, selectedClient: ClientResponse) => {
      setIsEditingExistingContract(true);
      setTipoContratacao(contract.tipoContratacao ?? '');
      setValorTotalMensal(contract.valorTotalMensal ?? 0);
      setDiaVencimento(contract.diaVencimento ?? 10);
      setNumeroP2B(contract.numeroP2B ?? '');
      setComprometimentoMinimo(contract.comprometimentoMinimo?.toString() ?? '0');
      const contractFastchip = contract.fastchip ?? false;
      setFastchip(contractFastchip);
      setIeBypassConfirmed(contract.ieBypassedAtCreation ?? false);
      setIeBypassReason(contract.ieBypassReason ?? '');
      setTipoFatura(contract.tipoFatura ?? 'Mensal');
      setEnvioFatura(contract.envioFatura ?? 'Email');
      setOrigemVenda(contract.origemVenda ?? 'Site');
      setToken(hydrateSaleTokenFromContract(contract.token, contractFastchip));

      if (contract.deliveryAddress) {
        setCep(formatCepForInput(contract.deliveryAddress.cep));
        setCidade(contract.deliveryAddress.cidade ?? '');
        setEstado(contract.deliveryAddress.estado ?? '');
        setEndereco(contract.deliveryAddress.endereco ?? '');
        setNumero(contract.deliveryAddress.numero ?? '');
        setBairro(contract.deliveryAddress.bairro ?? '');
        setComplemento(contract.deliveryAddress.complemento ?? '');
        setReferencia(contract.deliveryAddress.referencia ?? '');
      }

      if (contract.administrator) {
        setAdminNomeCompleto(contract.administrator.nomeCompleto ?? '');
        setAdminCpf(contract.administrator.cpf ?? '');
        setAdminRg(contract.administrator.rg ?? '');
        setAdminEmail(contract.administrator.email ?? '');
        setAdminTelefone1(contract.administrator.telefone1 ?? '');
        setAdminTelefone2(contract.administrator.telefone2 ?? '');
        setAdminSource('manual');
      }

      setRepresentantes(
        (contract.representatives ?? []).map((r) => ({
          id: r.id,
          nome: r.nome ?? '',
          cpf: r.cpf ?? '',
          rg: r.rg ?? '',
          email: r.email ?? '',
          telefone: normalizeRepresentativePhoneInput(r.telefone ?? ''),
          manual: false,
        }))
      );

      setRecebedores(
        (contract.authorizedReceivers ?? []).map((r) => ({
          id: r.id,
          nome: r.nome ?? '',
          qualificacao: r.qualificacao ?? '',
          cpf: r.cpf ?? '',
          rg: r.rg ?? '',
          email: r.email ?? '',
          telefone: r.telefone ?? '',
          celular: r.celular ?? '',
          manual: false,
        }))
      );

      setTestemunhas(
        (contract.witnesses ?? []).map((t) => ({
          id: t.id ?? '',
          nome: t.nome ?? '',
          cpf: t.cpf ?? '',
          rg: t.rg ?? '',
        }))
      );

      const linhasCarregadas = (contract.linhas ?? []);
      // Em edicao, infere temSuspensao a partir do estado atual das linhas.
      const jaTemSuspensao = linhasCarregadas.some(
        (l) => (l.requerSuspensao ?? false) || (l.excluirDoContrato ?? false)
      );
      setTemSuspensao(jaTemSuspensao ? true : false);

      const linhasForm = linhasCarregadas.map((l) => ({
        id: l.id,
        negociacao: l.negociacao ?? '',
        ddd: l.ddd ?? '',
        numero: l.numero ?? '',
        chipCcid: l.chipCcid ?? '',
        tipoVenda: l.tipoVenda ?? '',
        planId: l.planId ?? '',
        planName: l.planName ?? l.plano,
        plano: l.planName ?? l.plano ?? '',
        aparelho: l.aparelho ?? '',
        handsetDeviceId: (l as { handsetDeviceId?: string }).handsetDeviceId,
        aparelhoValorTotal: (l as { aparelhoValorTotal?: number }).aparelhoValorTotal,
        aparelhoValorMensal: (l as { aparelhoValorMensal?: number }).aparelhoValorMensal,
        valorChip: l.valorChip ?? 0,
        valorMensal: (l as { valorTotalMensal?: number }).valorTotalMensal ?? l.valorMensal ?? 0,
        valorTotalMensal: (l as { valorTotalMensal?: number }).valorTotalMensal ?? l.valorMensal ?? 0,
        quantidade: l.quantidade ?? 1,
        ofertas: l.ofertas ?? [],
        excluirDoContrato: l.excluirDoContrato ?? false,
        requerSuspensao: l.requerSuspensao ?? false,
        trocaLinha: l.trocaLinha ?? false,
        meuTimLogin: (l as { meuTimLogin?: string }).meuTimLogin ?? '',
        meuTimSenha: (l as { meuTimSenha?: string }).meuTimSenha ?? '',
        protocolo: (l as { protocolo?: string }).protocolo ?? '',
        seraUsadaPeloCliente: (l as { seraUsadaPeloCliente?: boolean | null }).seraUsadaPeloCliente ?? null,
        ehPortabilidade: (l as { ehPortabilidade?: boolean }).ehPortabilidade ?? false,
        portabilidadeNomeCompleto: (l as { portabilidadeNomeCompleto?: string }).portabilidadeNomeCompleto ?? '',
        portabilidadeCpf: (l as { portabilidadeCpf?: string }).portabilidadeCpf ?? '',
        portabilidadeOperadora: (l as { portabilidadeOperadora?: string }).portabilidadeOperadora ?? '',
        portabilidadeLinha: (l as { portabilidadeLinha?: string }).portabilidadeLinha ?? '',
        portabilidadeCnpjCedente: (l as { portabilidadeCnpjCedente?: string }).portabilidadeCnpjCedente ?? '',
        portabilidadeRazaoSocialCedente:
          (l as { portabilidadeRazaoSocialCedente?: string }).portabilidadeRazaoSocialCedente ?? '',
        portabilidadeRepresentantes:
          (l as { portabilidadeRepresentantes?: { nome: string; cpf: string }[] }).portabilidadeRepresentantes ?? [],
        isEsim: (l as { isEsim?: boolean }).isEsim ?? false,
        sourceContractId: contract.id,
        lineOriginContractId: contract.id,
      }));
      setLinhas(linhasForm);
      setLinhasParaDesativar(collectLinhasParaDesativarIds(linhasForm));

      const isMei =
        selectedClient.naturezaJuridica?.includes('2135') ||
        selectedClient.naturezaJuridica?.includes('Empresário (Individual)');
      if (!isMei && (selectedClient as { hasActiveInscricaoEstadual?: boolean }).hasActiveInscricaoEstadual === false) {
        setNeedsIeMonitoring(true);
      } else {
        setNeedsIeMonitoring(false);
      }
    },
    []
  );

  return {
    tipoFatura,
    setTipoFatura,
    envioFatura,
    setEnvioFatura,
    origemVenda,
    setOrigemVenda,
    cep,
    setCep,
    cidade,
    setCidade,
    estado,
    setEstado,
    endereco,
    setEndereco,
    numero,
    setNumero,
    bairro,
    setBairro,
    complemento,
    setComplemento,
    referencia,
    setReferencia,
    cepLoading,
    setCepLoading,
    adminNomeCompleto,
    setAdminNomeCompleto,
    adminCpf,
    setAdminCpf,
    adminRg,
    setAdminRg,
    adminEmail,
    setAdminEmail,
    adminTelefone1,
    setAdminTelefone1,
    adminTelefone2,
    setAdminTelefone2,
    adminSource,
    setAdminSource,
    tipoContratacao,
    setTipoContratacao,
    valorTotalMensal,
    setValorTotalMensal,
    diaVencimento,
    setDiaVencimento,
    numeroP2B,
    setNumeroP2B,
    comprometimentoMinimo,
    setComprometimentoMinimo,
    fastchip,
    setFastchip,
    temSuspensao,
    setTemSuspensao,
    representantes,
    setRepresentantes,
    representanteManual,
    setRepresentanteManual,
    selectedRepresentante,
    setSelectedRepresentante,
    recebedores,
    setRecebedores,
    recebedorManual,
    setRecebedorManual,
    testemunhas,
    setTestemunhas,
    testemunhaAtual,
    setTestemunhaAtual,
    linhas,
    setLinhas,
    linhaAtual,
    setLinhaAtual,
    linhasParaDesativar,
    setLinhasParaDesativar,
    editingLinha,
    setEditingLinha,
    showEditLinhaModal,
    setShowEditLinhaModal,
    linhaEditando,
    setLinhaEditando,
    needsIeMonitoring,
    setNeedsIeMonitoring,
    ieBypassConfirmed,
    setIeBypassConfirmed,
    ieBypassReason,
    setIeBypassReason,
    isEditingExistingContract,
    setIsEditingExistingContract,
    tabValidationError,
    setTabValidationError,
    error,
    setError,
    isPrimeiraVenda,
    setIsPrimeiraVenda,
    token,
    setToken,
    reset,
    loadFromContract,
  };
}
